import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import i18n from '../../lib/i18n';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import PeriodFormModal from '../../components/PeriodFormModal';
import {
  CashflowReport,
  BalancesReport,
  CashierReport,
  ProfitReport,
  ExpensesReport,
  PnLReport,
  DailyActivityReport,
} from '../reports';

type ReportType = 'cashflow' | 'balances' | 'cashier' | 'profit' | 'expenses' | 'pnl' | 'daily';
type ExportFormat = 'csv' | 'pdf' | 'xlsx';

interface Period {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed';
}

export default function ReportsPage() {
  const { t } = useTranslation(['reports', 'common']);
  const queryClient = useQueryClient();
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportType = (searchParams.get('tab') as ReportType) || 'cashflow';
  const selectedPeriodId = searchParams.get('periodId') || '';
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [showNewPeriodModal, setShowNewPeriodModal] = useState(false);

  const setReportType = useCallback((type: ReportType) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (type === 'cashflow') next.delete('tab'); else next.set('tab', type);
      // Clear tab-specific params when switching tabs
      next.delete('viewMode');
      next.delete('page');
      next.delete('days');
      next.delete('date');
      next.delete('cashDeskId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setSelectedPeriodId = useCallback((id: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) next.set('periodId', id); else next.delete('periodId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { data: periodsData } = useQuery({
    queryKey: ['periods', currentEntity?.id],
    queryFn: () => api.admin.periods.list(currentEntity!.id),
    enabled: !!currentEntity,
  });

  const periods: Period[] = periodsData?.data || [];
  const selectedPeriod = periods.find(p => p.id === selectedPeriodId);

  const canViewProfit = user?.role === 'admin' || user?.role === 'accountant';
  const canExport = user?.role === 'admin' || user?.role === 'accountant';
  const canCreatePeriod = user?.role === 'admin' || user?.role === 'accountant';

  const balancesViewMode = (searchParams.get('balancesView') as 'current' | 'daily') || 'daily';
  const balancesDays = parseInt(searchParams.get('balancesDays') || '14', 10) || 14;

  const setBalancesViewMode = useCallback((mode: 'current' | 'daily') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (mode === 'daily') next.delete('balancesView'); else next.set('balancesView', mode);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setBalancesDays = useCallback((d: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (d === 14) next.delete('balancesDays'); else next.set('balancesDays', String(d));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleExport = useCallback(async (format: ExportFormat) => {
    if (!currentEntity || exporting) return;

    setExporting(format);
    try {
      const filters: Record<string, any> = {};
      if (selectedPeriodId) filters.periodId = selectedPeriodId;
      if (reportType === 'balances' && balancesViewMode === 'daily') {
        filters.daily = true;
        filters.days = balancesDays;
      }
      if (reportType === 'daily') {
        const today = new Date().toISOString().split('T')[0];
        filters.date = searchParams.get('date') || today;
        const dateTo = searchParams.get('dateTo');
        if (dateTo && dateTo !== filters.date) filters.dateTo = dateTo;
        const dailyCashDeskId = searchParams.get('cashDeskId');
        if (dailyCashDeskId) filters.cashDeskId = dailyCashDeskId;
      }

      // Extract base language code (e.g., 'en-US' -> 'en') and validate against supported languages
      const supportedLanguages = ['en', 'ru', 'es'];
      const baseLang = (i18n.language || 'en').split('-')[0];
      const language = supportedLanguages.includes(baseLang) ? baseLang : 'en';

      const response = await fetch(`/api/reports/export/${reportType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Entity-Id': currentEntity.id,
        },
        credentials: 'include',
        body: JSON.stringify({
          format,
          language,
          filters: Object.keys(filters).length > 0 ? filters : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const filename = `${reportType}-${new Date().toISOString().split('T')[0]}.${format}`;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  }, [currentEntity, reportType, selectedPeriodId, exporting, balancesViewMode, balancesDays, toast, searchParams]);

  if (!currentEntity) return null;

  return (
    <div>
      <div className="page-header reports-header">
        <h1 className="page-title">{t('title')}</h1>
        <div className="period-selector">
          <label>{t('period')}:</label>
          <select
            value={selectedPeriodId}
            onChange={(e) => {
              if (e.target.value === '__new__') {
                setShowNewPeriodModal(true);
                e.target.value = selectedPeriodId;
              } else {
                setSelectedPeriodId(e.target.value);
              }
            }}
            className="form-input"
          >
            <option value="">{t('allTime')}</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.status === 'closed' ? `(${t('common:status.closed')})` : ''}
              </option>
            ))}
            {canCreatePeriod && (
              <option value="__new__">+ {t('addPeriod')}</option>
            )}
          </select>
          {selectedPeriod && (
            <span className="period-dates">
              {formatDate(selectedPeriod.startDate)} - {formatDate(selectedPeriod.endDate)}
            </span>
          )}
        </div>
      </div>

      {showNewPeriodModal && currentEntity && (
        <PeriodFormModal
          entityId={currentEntity.id}
          onClose={() => setShowNewPeriodModal(false)}
          onSuccess={(createdPeriodId) => {
            queryClient.invalidateQueries({ queryKey: ['periods'] });
            if (createdPeriodId) {
              setSelectedPeriodId(createdPeriodId);
            }
            setShowNewPeriodModal(false);
          }}
        />
      )}

      <div className="card mb-4">
        <div className="report-header-row">
          <div className="report-tabs">
            <button
              className={`report-tab ${reportType === 'cashflow' ? 'active' : ''}`}
              onClick={() => setReportType('cashflow')}
            >
              {t('tabs.cashflow')}
            </button>
            <button
              className={`report-tab ${reportType === 'balances' ? 'active' : ''}`}
              onClick={() => setReportType('balances')}
            >
              {t('tabs.balances')}
            </button>
            <button
              className={`report-tab ${reportType === 'cashier' ? 'active' : ''}`}
              onClick={() => setReportType('cashier')}
            >
              {t('tabs.cashier')}
            </button>
            <button
              className={`report-tab ${reportType === 'daily' ? 'active' : ''}`}
              onClick={() => setReportType('daily')}
            >
              {t('tabs.daily')}
            </button>
            {canViewProfit && (
              <>
                <button
                  className={`report-tab ${reportType === 'profit' ? 'active' : ''}`}
                  onClick={() => setReportType('profit')}
                >
                  {t('tabs.profit')}
                </button>
                <button
                  className={`report-tab ${reportType === 'expenses' ? 'active' : ''}`}
                  onClick={() => setReportType('expenses')}
                >
                  {t('tabs.expenses')}
                </button>
                <button
                  className={`report-tab ${reportType === 'pnl' ? 'active' : ''}`}
                  onClick={() => setReportType('pnl')}
                >
                  {t('tabs.pnl')}
                </button>
              </>
            )}
          </div>
          {canExport && (
            <div className="export-buttons">
              <button
                className="btn btn-secondary export-btn"
                onClick={() => handleExport('xlsx')}
                disabled={exporting !== null}
              >
                {exporting === 'xlsx' ? '...' : 'Excel'}
              </button>
              <button
                className="btn btn-secondary export-btn"
                onClick={() => handleExport('csv')}
                disabled={exporting !== null}
              >
                {exporting === 'csv' ? '...' : 'CSV'}
              </button>
              {!(reportType === 'balances' && balancesViewMode === 'daily') && (
                <button
                  className="btn btn-secondary export-btn"
                  onClick={() => handleExport('pdf')}
                  disabled={exporting !== null}
                >
                  {exporting === 'pdf' ? '...' : 'PDF'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {reportType === 'cashflow' && <CashflowReport entityId={currentEntity.id} periodId={selectedPeriodId} />}
      {reportType === 'balances' && <BalancesReport entityId={currentEntity.id} periodId={selectedPeriodId} viewMode={balancesViewMode} setViewMode={setBalancesViewMode} days={balancesDays} setDays={setBalancesDays} />}
      {reportType === 'cashier' && <CashierReport entityId={currentEntity.id} periodId={selectedPeriodId} />}
      {reportType === 'daily' && <DailyActivityReport entityId={currentEntity.id} />}
      {reportType === 'profit' && <ProfitReport entityId={currentEntity.id} periodId={selectedPeriodId} />}
      {reportType === 'expenses' && <ExpensesReport entityId={currentEntity.id} periodId={selectedPeriodId} />}
      {reportType === 'pnl' && <PnLReport entityId={currentEntity.id} periodId={selectedPeriodId} />}

      <style>{`
        .report-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .report-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .report-tab {
          padding: 10px 20px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          cursor: pointer;
          transition: all 0.15s ease;
          font-weight: 500;
          color: var(--color-text);
        }
        .report-tab:hover {
          background: var(--color-surface-hover);
          border-color: var(--color-border);
        }
        .report-tab.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }
        [data-theme="dark"] .report-tab.active {
          color: var(--ctp-crust);
        }
        .export-buttons {
          display: flex;
          gap: 8px;
        }
        .export-btn {
          min-width: 60px;
          padding: 8px 16px;
          font-size: 13px;
        }
        .export-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .period-selector {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .period-selector label {
          font-weight: 500;
          color: var(--color-text-muted);
        }
        .period-selector select {
          width: auto;
          min-width: 200px;
        }
        .period-dates {
          font-size: 13px;
          color: var(--color-text-muted);
        }
        .reports-header {
          justify-content: flex-start;
          gap: 24px;
        }
      `}</style>
    </div>
  );
}
